/** 客户端页面布局。样板只覆盖所属团队页。 */

export const ENTERPRISE_SPACE_PAGE_ID = 'enterprise-space';

export type EnterpriseSpaceBlockId =
  | 'entry-card'
  | 'team-header'
  | 'rename-team'
  | 'invite-link'
  | 'leave-team'
  | 'members'
  | 'seats';

/** 自定义说明的可见身份。 */
export type EnterpriseSpaceNoteAudience = 'unjoined' | 'joined' | 'members' | 'managers' | 'owner';

/** 当前访问者相对该团队的身份。 */
export type EnterpriseSpaceViewer = 'unjoined' | 'member' | 'admin' | 'owner';

export type EnterpriseSpaceFieldDefinition = {
  key: string;
  label: string;
  defaultValue: string;
  multiline: boolean;
  maxLength: number;
};

export type EnterpriseSpaceBlockDefinition = {
  id: EnterpriseSpaceBlockId;
  label: string;
  /** 默认对哪些身份显示。后台可以改。 */
  defaultAudiences: Record<EnterpriseSpaceViewer, boolean>;
  fields: readonly EnterpriseSpaceFieldDefinition[];
};

export type EnterpriseSpaceBlock = {
  id: EnterpriseSpaceBlockId;
  visible: boolean;
  audiences: Record<EnterpriseSpaceViewer, boolean>;
  fields: Record<string, string>;
};

export type EnterpriseSpaceNote = {
  id: string;
  title: string;
  body: string;
  audience: EnterpriseSpaceNoteAudience;
  visible: boolean;
};

export type EnterpriseSpaceLayout = {
  pageId: typeof ENTERPRISE_SPACE_PAGE_ID;
  blocks: EnterpriseSpaceBlock[];
  notes: EnterpriseSpaceNote[];
  updatedAt: string;
};

export const ENTERPRISE_SPACE_NOTE_AUDIENCES: ReadonlyArray<{id: EnterpriseSpaceNoteAudience; label: string}> = [
  {id: 'unjoined', label: '尚未加入团队的人'},
  {id: 'joined', label: '已加入团队的所有人'},
  {id: 'members', label: '仅普通成员'},
  {id: 'managers', label: '最高管理员和团队管理员'},
  {id: 'owner', label: '仅最高管理员'},
];

export const ENTERPRISE_SPACE_VIEWERS: ReadonlyArray<{id: EnterpriseSpaceViewer; label: string}> = [
  {id: 'unjoined', label: '尚未加入团队'},
  {id: 'member', label: '团队普通成员'},
  {id: 'admin', label: '团队管理员'},
  {id: 'owner', label: '最高管理员'},
];

const NOTE_LIMIT = 30;
const NOTE_TITLE_LIMIT = 80;
const NOTE_BODY_LIMIT = 1000;

export const ENTERPRISE_SPACE_BLOCKS: readonly EnterpriseSpaceBlockDefinition[] = [
  {
    id: 'entry-card',
    label: '未加入团队时的入口卡',
    defaultAudiences: {unjoined: true, member: false, admin: false, owner: false},
    fields: [
      {key: 'title', label: '标题', defaultValue: '团队空间', multiline: false, maxLength: 40},
      {key: 'description', label: '说明', defaultValue: '创建团队，或通过名称、团队 ID 或邀请链接加入已有团队。', multiline: true, maxLength: 240},
      {key: 'applyButton', label: '申请按钮', defaultValue: '申请加入团队', multiline: false, maxLength: 24},
      {key: 'createButton', label: '创建按钮', defaultValue: '创建团队', multiline: false, maxLength: 24},
    ],
  },
  {
    id: 'team-header',
    label: '团队页头',
    defaultAudiences: {unjoined: false, member: true, admin: true, owner: true},
    fields: [
      {key: 'description', label: '标题旁的说明', defaultValue: '', multiline: true, maxLength: 240},
    ],
  },
  {
    id: 'rename-team',
    label: '修改团队名称',
    defaultAudiences: {unjoined: false, member: false, admin: false, owner: true},
    fields: [
      {key: 'label', label: '按钮文案', defaultValue: '修改团队名称', multiline: false, maxLength: 24},
    ],
  },
  {
    id: 'invite-link',
    label: '复制邀请链接',
    defaultAudiences: {unjoined: false, member: false, admin: true, owner: true},
    fields: [
      {key: 'label', label: '按钮文案', defaultValue: '复制邀请链接', multiline: false, maxLength: 24},
    ],
  },
  {
    id: 'leave-team',
    label: '退出团队',
    defaultAudiences: {unjoined: false, member: true, admin: true, owner: true},
    fields: [
      {key: 'label', label: '按钮文案', defaultValue: '退出团队', multiline: false, maxLength: 24},
    ],
  },
  {
    id: 'members',
    label: '团队成员与授权',
    defaultAudiences: {unjoined: false, member: true, admin: true, owner: true},
    fields: [
      {key: 'title', label: '标题', defaultValue: '团队成员与授权', multiline: false, maxLength: 40},
    ],
  },
  {
    id: 'seats',
    label: '服务功能席位分配',
    defaultAudiences: {unjoined: false, member: false, admin: true, owner: true},
    fields: [
      {key: 'title', label: '标题', defaultValue: '服务功能席位分配', multiline: false, maxLength: 40},
    ],
  },
];

const BLOCK_BY_ID = new Map(ENTERPRISE_SPACE_BLOCKS.map((item) => [item.id, item]));
const AUDIENCE_IDS = new Set(ENTERPRISE_SPACE_NOTE_AUDIENCES.map((item) => item.id));

/**
 * 去掉首尾空白并截断到上限。
 * @param value 原始文本
 * @param maxLength 最大长度
 */
function clipText(value: unknown, maxLength: number): string {
  return String(value ?? '').replace(/\u0000/g, '').trim().slice(0, maxLength);
}

/**
 * 所属团队页的默认布局。未保存过配置时客户端和后台都用它。
 */
export function defaultEnterpriseSpaceLayout(): EnterpriseSpaceLayout {
  return {
    pageId: ENTERPRISE_SPACE_PAGE_ID,
    blocks: ENTERPRISE_SPACE_BLOCKS.map((definition) => ({
      id: definition.id,
      visible: true,
      audiences: {...definition.defaultAudiences},
      fields: Object.fromEntries(definition.fields.map((field) => [field.key, field.defaultValue])),
    })),
    notes: [],
    updatedAt: '',
  };
}

/**
 * 把任意输入收成合法布局。内置板块不能增删，自定义说明可以增删。
 * @param input 后台提交或库里读出的值
 * @param updatedAt 保存时间；缺省沿用输入里的时间
 */
export function normalizeEnterpriseSpaceLayout(input: unknown, updatedAt?: string): EnterpriseSpaceLayout {
  const source = input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};
  const savedBlocks = new Map<string, Record<string, unknown>>();
  if (Array.isArray(source.blocks)) {
    for (const item of source.blocks) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      const id = String(row.id ?? '');
      if (BLOCK_BY_ID.has(id as EnterpriseSpaceBlockId) && !savedBlocks.has(id)) savedBlocks.set(id, row);
    }
  }
  const blocks = ENTERPRISE_SPACE_BLOCKS.map((definition) => {
    const saved = savedBlocks.get(definition.id);
    const savedFields = saved?.fields && typeof saved.fields === 'object' && !Array.isArray(saved.fields)
      ? saved.fields as Record<string, unknown>
      : {};
    const fields: Record<string, string> = {};
    for (const field of definition.fields) {
      const raw = Object.prototype.hasOwnProperty.call(savedFields, field.key) ? savedFields[field.key] : field.defaultValue;
      fields[field.key] = clipText(raw, field.maxLength);
    }
    const savedAudiences = saved?.audiences && typeof saved.audiences === 'object' && !Array.isArray(saved.audiences)
      ? saved.audiences as Record<string, unknown>
      : null;
    const audiences = {} as Record<EnterpriseSpaceViewer, boolean>;
    for (const viewer of ENTERPRISE_SPACE_VIEWERS) {
      if (savedAudiences && Object.prototype.hasOwnProperty.call(savedAudiences, viewer.id)) audiences[viewer.id] = savedAudiences[viewer.id] !== false;
      else if (saved?.visible === false) audiences[viewer.id] = false;
      else audiences[viewer.id] = definition.defaultAudiences[viewer.id];
    }
    return {
      id: definition.id,
      visible: ENTERPRISE_SPACE_VIEWERS.some((viewer) => audiences[viewer.id]),
      audiences,
      fields,
    };
  });
  const notes: EnterpriseSpaceNote[] = [];
  const seen = new Set<string>();
  if (Array.isArray(source.notes)) {
    for (const item of source.notes) {
      if (notes.length >= NOTE_LIMIT) break;
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      const title = clipText(row.title, NOTE_TITLE_LIMIT);
      const body = clipText(row.body, NOTE_BODY_LIMIT);
      if (!title && !body) continue;
      const audience = String(row.audience ?? '') as EnterpriseSpaceNoteAudience;
      if (!AUDIENCE_IDS.has(audience)) continue;
      let id = clipText(row.id, 48);
      if (!/^note_[a-z0-9]{8,32}$/.test(id) || seen.has(id)) id = createEnterpriseSpaceNoteId(seen);
      seen.add(id);
      notes.push({id, title, body, audience, visible: row.visible !== false});
    }
  }
  const stamp = clipText(updatedAt ?? source.updatedAt, 40);
  return {pageId: ENTERPRISE_SPACE_PAGE_ID, blocks, notes, updatedAt: stamp};
}

/**
 * 生成不重复的说明标识。
 * @param used 已经占用的标识
 */
export function createEnterpriseSpaceNoteId(used: Iterable<string> = []): string {
  const taken = new Set(used);
  const alphabet = '0123456789abcdef';
  for (let attempt = 0; attempt < 8; attempt += 1) {
    let body = '';
    for (let index = 0; index < 12; index += 1) body += alphabet[Math.floor(Math.random() * alphabet.length)];
    const id = `note_${body}`;
    if (!taken.has(id)) return id;
  }
  return `note_${Date.now().toString(16)}${taken.size.toString(16)}`.slice(0, 37);
}

/**
 * 读取板块文案。缺省时回到内置默认值。
 * @param layout 页面布局
 * @param blockId 板块标识
 * @param fieldKey 字段名
 */
export function enterpriseSpaceBlockText(layout: EnterpriseSpaceLayout, blockId: EnterpriseSpaceBlockId, fieldKey: string): string {
  const definition = BLOCK_BY_ID.get(blockId);
  const fallback = definition?.fields.find((field) => field.key === fieldKey)?.defaultValue ?? '';
  const block = layout.blocks.find((item) => item.id === blockId);
  const value = block?.fields[fieldKey];
  return typeof value === 'string' ? value : fallback;
}

/**
 * 这个板块是否对当前身份显示。
 * @param layout 页面布局
 * @param blockId 板块标识
 * @param viewer 访问者身份
 */
export function enterpriseSpaceBlockVisible(layout: EnterpriseSpaceLayout, blockId: EnterpriseSpaceBlockId, viewer: EnterpriseSpaceViewer): boolean {
  const definition = BLOCK_BY_ID.get(blockId);
  const block = layout.blocks.find((item) => item.id === blockId);
  if (!block) return definition?.defaultAudiences[viewer] ?? false;
  return block.audiences[viewer] === true;
}

/**
 * 这条说明是否应该出现在当前访问者面前。
 * @param note 自定义说明
 * @param viewer 访问者身份
 */
export function enterpriseSpaceNoteVisibleTo(note: EnterpriseSpaceNote, viewer: EnterpriseSpaceViewer): boolean {
  if (!note.visible) return false;
  if (note.audience === 'unjoined') return viewer === 'unjoined';
  if (note.audience === 'joined') return viewer !== 'unjoined';
  if (note.audience === 'members') return viewer === 'member';
  if (note.audience === 'managers') return viewer === 'admin' || viewer === 'owner';
  return viewer === 'owner';
}
